/**
 * Setores e unidades dos municípios de uma empresa, dentro do detalhe da empresa — Nexora Grid (frontend, US-1904).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 1.1.0
 *
 * O código é a dimensão `unidade` do tópico MQTT e não muda; nome e tipo se corrigem com a versão lida. Setor em uso
 * (ativo, dispositivo ou telemetria) não se exclui (inative). Os usados sem cadastro aparecem para regularizar.
 * v1.1 (US-4604, Sprint 46): bloco com título e contagem, tabela densa no bloco, formulário em linha com validação ao
 * sair do campo; mesmas chamadas e permissões.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { Campo, Esqueleto, Selo } from "../componentes/Tela";
import "./estrutura.css";

export const TIPOS_SETOR: Record<string, string> = { setor_abastecimento: "Setor de abastecimento", estacao_tratamento: "Estação de tratamento",
  captacao: "Captação", elevatoria: "Elevatória", reservacao: "Reservação", outro: "Outro" };
type Setor = { municipio: string; codigo: string; nome: string; tipo: string; ativa: boolean; versao: number; ativos: number;
  dispositivos: number; equipamentos_no_hub: number };
type Uso = { municipio: string; codigo: string; ativos: number; dispositivos: number; equipamentos_no_hub: number };
type Form = { municipio: string; codigo: string; nome: string; tipo: string; versao: number | null };
const VAZIO: Form = { municipio: "", codigo: "", nome: "", tipo: "setor_abastecimento", versao: null };
type Campos = "municipio" | "codigo" | "nome";

function erroDe(f: Form, c: Campos): string | null {
  if (c === "municipio") return f.municipio ? null : "Escolha o município (cadastre-o antes em Municípios).";
  if (c === "codigo") return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(f.codigo) ? null : "Use letras minúsculas, números, - ou _ (ex.: rse-001).";
  return f.nome.trim().length >= 2 ? null : "Escreva o nome do setor (2 letras ou mais).";
}

export function Setores({ empresa, edita, aoMudar }: { empresa: string; edita: boolean; aoMudar?: () => void }) {
  const [dados, setDados] = useState<{ setores: Setor[]; nao_cadastrados: Uso[] } | null>(null);
  const [municipios, setMunicipios] = useState<{ codigo: string; nome: string }[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [tocados, setTocados] = useState<Set<Campos>>(new Set());
  const base = `/v1/admin/empresas/${empresa}/setores`;
  const carregar = useCallback(() => {
    api<{ setores: Setor[]; nao_cadastrados: Uso[] }>(base).then(setDados).catch(() => setDados({ setores: [], nao_cadastrados: [] }));
    api<{ municipios: { codigo: string; nome: string; cadastrado: boolean }[] }>(`/v1/municipios?empresa=${empresa}`)
      .then((r) => setMunicipios(r.municipios.filter((m) => m.cadastrado))).catch(() => setMunicipios([]));
  }, [base, empresa]);
  useEffect(carregar, [carregar]);
  const executar = async (acao: () => Promise<unknown>, ok: string) => {
    try { await acao(); setAviso(ok); setForm(null); carregar(); aoMudar?.(); }
    catch (e) { setAviso(e instanceof ErroApi ? `Não foi possível: ${e.message}` : "Falha de rede."); }
  };
  const salvar = (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    const { municipio, codigo, nome, tipo, versao } = form;
    void executar(() => (versao
      ? api(`${base}/${municipio}/${codigo}`, { metodo: "PUT", corpo: { versao, nome, tipo } })
      : api(base, { corpo: { municipio, codigo, nome, tipo } })), `Setor ${codigo} salvo.`);
  };
  const abrir = (f: Form) => { carregar(); setForm(f); setTocados(new Set()); };  // município recém-cadastrado entra na lista
  const tocar = (c: Campos) => setTocados((t) => new Set(t).add(c));
  const erro = (c: Campos) => (form && tocados.has(c) ? erroDe(form, c) : null);
  const nomeMunicipio = (c: string) => municipios.find((m) => m.codigo === c)?.nome ?? c;
  const ativos = dados?.setores.filter((s) => s.ativa).length ?? 0;
  return (
    <section className="filiais tl-bloco admin-secao" aria-label="Setores e unidades">
      <header>
        <h3>Setores e unidades</h3>
        {dados && <span className="tl-subtitulo">{dados.setores.length} cadastrados · {ativos} ativos</span>}
        {edita && !form && <div className="tl-acoes"><button type="button" className="nx-botao" onClick={() => abrir(VAZIO)}>Novo setor</button></div>}
      </header>
      {aviso && <p className="admin-aviso" role="status">{aviso}</p>}
      {!dados ? <Esqueleto linhas={3} /> : (
        <>
          <div className="admin-tabela">
            <table className="tl-tabela">
              <thead><tr><th scope="col">Município</th><th scope="col">Código</th><th scope="col">Nome</th><th scope="col">Tipo</th><th scope="col">Situação</th>
                <th scope="col">Ativos</th><th scope="col">Dispositivos</th><th scope="col">No hub (7 dias)</th>
                {edita && <th scope="col"><span className="visualmente-oculto">Ações</span></th>}</tr></thead>
              <tbody>
                {dados.setores.map((s) => (
                  <tr key={`${s.municipio}/${s.codigo}`}>
                    <td>{nomeMunicipio(s.municipio)}</td><td className="tl-mono">{s.codigo}</td><td>{s.nome}</td><td>{TIPOS_SETOR[s.tipo] ?? s.tipo}</td>
                    <td><Selo tom={s.ativa ? "ok" : "ruim"}>{s.ativa ? "Ativo" : "Inativo"}</Selo></td>
                    <td className="tl-mono">{s.ativos}</td><td className="tl-mono">{s.dispositivos}</td><td className="tl-mono">{s.equipamentos_no_hub}</td>
                    {edita && (
                      <td className="acoes-linha">
                        <button type="button" className="tl-fantasma botao-linha" onClick={() => { setForm({ ...s, versao: s.versao }); setTocados(new Set()); }}>Editar</button>
                        <button type="button" className="tl-fantasma botao-linha" onClick={() => void executar(() => api(`${base}/${s.municipio}/${s.codigo}/${s.ativa ? "inativar" : "reativar"}`, { corpo: {} }),
                          s.ativa ? "Setor inativado." : "Setor reativado.")}>{s.ativa ? "Inativar" : "Reativar"}</button>
                        {!s.ativos && !s.dispositivos && !s.equipamentos_no_hub && (
                          <button type="button" className="tl-fantasma botao-linha" onClick={() => void executar(() => api(`${base}/${s.municipio}/${s.codigo}`, { metodo: "DELETE" }), "Setor excluído.")}>Excluir</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!dados.setores.length && <tr><td colSpan={9} className="vazio">Nenhum setor cadastrado{edita ? ": use \"Novo setor\"." : "."}</td></tr>}
              </tbody>
            </table>
          </div>
          {(dados.nao_cadastrados.length > 0 || (edita && form)) && (
            <div className="admin-corpo">
              {dados.nao_cadastrados.length > 0 && (
                <div className="admin-pendentes">
                  <b>Em uso e sem cadastro:</b>
                  {dados.nao_cadastrados.map((n) => (
                    <span key={`${n.municipio}/${n.codigo}`} className="nao-cadastrada">
                      <span className="tl-mono">{n.municipio ? `${n.municipio}/` : ""}{n.codigo}</span>{" "}
                      <small className="nx-suave">({n.ativos} ativos · {n.dispositivos} disp. · {n.equipamentos_no_hub} no hub)</small>
                      {edita && <button type="button" className="tl-fantasma botao-linha" onClick={() => abrir({ ...VAZIO, municipio: n.municipio, codigo: n.codigo })}>Cadastrar</button>}
                    </span>
                  ))}
                </div>
              )}
              {edita && form && (
                <form className="form-linha" onSubmit={salvar} aria-label="Setor">
                  <Campo id="set-municipio" rotulo="Município do setor" erro={erro("municipio")}>
                    <select id="set-municipio" required disabled={form.versao !== null} value={form.municipio} onBlur={() => tocar("municipio")}
                      onChange={(e) => setForm({ ...form, municipio: e.target.value })}>
                      <option value="">—</option>{municipios.map((m) => <option key={m.codigo} value={m.codigo}>{m.nome}</option>)}
                    </select>
                  </Campo>
                  <Campo id="set-codigo" rotulo="Código do setor" erro={erro("codigo")} ajuda={form.versao !== null ? "Não muda (é o tópico MQTT)" : undefined}>
                    <input id="set-codigo" required pattern="[a-z0-9][a-z0-9_\-]{0,63}" readOnly={form.versao !== null} value={form.codigo} onBlur={() => tocar("codigo")}
                      onChange={(e) => setForm({ ...form, codigo: e.target.value.trim().toLowerCase() })} placeholder="ex.: rse-001" />
                  </Campo>
                  <Campo id="set-nome" rotulo="Nome do setor" erro={erro("nome")}>
                    <input id="set-nome" required minLength={2} maxLength={120} value={form.nome} onBlur={() => tocar("nome")}
                      onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                  </Campo>
                  <Campo id="set-tipo" rotulo="Tipo do setor">
                    <select id="set-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                      {Object.entries(TIPOS_SETOR).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                    </select>
                  </Campo>
                  <div className="tl-linha-acoes">
                    <button type="button" className="tl-fantasma" onClick={() => setForm(null)}>Cancelar</button>
                    <button type="submit" className="nx-botao nx-botao-principal">Salvar setor</button>
                  </div>
                </form>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
